import React, {createRef, forwardRef, memo, useEffect, useState} from "react";
import IconCalender from "../../data/Icons/leon-inmobiliaria-icons-kalender.svg";
import DatePicker, {registerLocale} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomHeader from "../FindImmobilieForm/CustomDatePicker/CustomHeader";
import CustomHeaderThreeMonths from "../FindImmobilieForm/CustomDatePicker/CustomHeaderThreeMonths";
import {useRouter} from "next/router";
import Button from "../common/Button";

import {de} from "date-fns/locale";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {objectsAttributes} from "../../models/objects";
import {fetchItem, getBookings, getObject, getSeasonDates} from "../../data/ReactQueries";
import {calcNormalRentPrice, calcZuZahlenBeiBuchungUndAnkunft} from "../CalculationHelper/CalcHelpers";
import SocialMediaButtonGroup from "./AnfrageFormComponents/SocialMediaButtonGroup";
import {Filter} from "../../data/Filter/Filter.config";
import {useTranslation} from "next-i18next";
import {season_dates} from "../../models/season_dates";
import {ScreenNumber, useMediaQuery} from "../../hooks/useMediaQuery";
import {booking} from "../../models/booking";

registerLocale("de", de);

const RESTRICTED_BOOKING_DATE_RANGE = {
	start: {
		month: 6,
		day: 27
	},
	end: {
		month: 9,
		day: 11
	}
};

function getStartOfDay(date: Date) {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number) {
	const newDate = getStartOfDay(date);
	newDate.setDate(newDate.getDate() + days);
	return newDate;
}

function getDateKey(date: Date) {
	const normalizedDate = getStartOfDay(date);
	const year = normalizedDate.getFullYear();
	const month = String(normalizedDate.getMonth() + 1).padStart(2, "0");
	const day = String(normalizedDate.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
}

function isSameDay(firstDate: Date | null, secondDate: Date | null) {
	if (!firstDate || !secondDate) {
		return false;
	}

	return getStartOfDay(firstDate).getTime() === getStartOfDay(secondDate).getTime();
}

function isSaturday(date: Date) {
	return date.getDay() === 6;
}

function isDateInRestrictedBookingSeason(date: Date) {
	const restrictedStartDate = getStartOfDay(new Date(
		date.getFullYear(),
		RESTRICTED_BOOKING_DATE_RANGE.start.month - 1,
		RESTRICTED_BOOKING_DATE_RANGE.start.day
	));
	const restrictedEndDate = getStartOfDay(new Date(
		date.getFullYear(),
		RESTRICTED_BOOKING_DATE_RANGE.end.month - 1,
		RESTRICTED_BOOKING_DATE_RANGE.end.day
	));
	const currentDate = getStartOfDay(date);

	if (restrictedStartDate.getTime() <= restrictedEndDate.getTime()) {
		return currentDate.getTime() >= restrictedStartDate.getTime() &&
			currentDate.getTime() <= restrictedEndDate.getTime();
	}

	return currentDate.getTime() >= restrictedStartDate.getTime() ||
		currentDate.getTime() <= restrictedEndDate.getTime();
}

function rangeTouchesRestrictedBookingSeason(start: Date, end: Date) {
	const cursorDate = getStartOfDay(start);
	const endDate = getStartOfDay(end);

	while (cursorDate.getTime() <= endDate.getTime()) {
		if (isDateInRestrictedBookingSeason(cursorDate)) {
			return true;
		}
		cursorDate.setDate(cursorDate.getDate() + 1);
	}

	return false;
}

function getStayInNights(start: Date, end: Date) {
	return Math.floor(
		(getStartOfDay(end).getTime() - getStartOfDay(start).getTime()) /
		(1000 * 60 * 60 * 24)
	);
}

function getBookingBoundaryDateKeys(bookings: booking[] | undefined) {
	const arrivalDateKeys = new Set<string>();
	const departureDateKeys = new Set<string>();

	bookings
		?.filter((currentBooking) => currentBooking.to_date > currentBooking.from_date)
		.forEach((currentBooking) => {
			arrivalDateKeys.add(getDateKey(new Date(currentBooking.from_date * 1000)));
			departureDateKeys.add(getDateKey(new Date(currentBooking.to_date * 1000)));
		});

	return {
		arrivalDateKeys,
		departureDateKeys
	};
}

function getFullyBookedDateIntervals(bookings: booking[] | undefined) {
	return bookings
		?.filter((currentBooking) => currentBooking.to_date > currentBooking.from_date)
		.map((currentBooking) => {
			const arrivalDate = getStartOfDay(new Date(currentBooking.from_date * 1000));
			const departureDate = getStartOfDay(new Date(currentBooking.to_date * 1000));

			return {
				start: addDays(arrivalDate, 1),
				end: addDays(departureDate, -1)
			};
		})
		.filter((dateInterval) => dateInterval.start.getTime() <= dateInterval.end.getTime())
		.sort((firstInterval, secondInterval) => firstInterval.start.getTime() - secondInterval.start.getTime());
}

function isDateInIntervals(date: Date, dateIntervals: { start: Date; end: Date }[] | undefined) {
	const currentDate = getStartOfDay(date).getTime();

	return Boolean(dateIntervals?.some((dateInterval) => {
		return currentDate >= getStartOfDay(dateInterval.start).getTime() &&
			currentDate <= getStartOfDay(dateInterval.end).getTime();
	}));
}

function getNextBookedArrivalDate(bookings: booking[] | undefined, date: Date) {
	const currentDate = getStartOfDay(date).getTime();

	return bookings
		?.filter((currentBooking) => currentBooking.to_date > currentBooking.from_date)
		.map((currentBooking) => getStartOfDay(new Date(currentBooking.from_date * 1000)))
		.filter((arrivalDate) => arrivalDate.getTime() > currentDate)
		.sort((firstDate, secondDate) => firstDate.getTime() - secondDate.getTime())[0] ?? null;
}

interface VermietungBuchungAnfrageFormProps {
	objectId: number;
	isBuchunganfragenPage?: boolean;
	gesamtPreis?: number;
	startDate: Date | null;
	onStartDateChange: (date: Date | null) => void;
	endDate: Date | null;
	onEndDateChange: (date: Date | null) => void;
	adultCount?: number;
	onAdultCountChange: (count: number) => void;
	kidsCount?: number;
	onKidsCountChange: (count: number) => void;
}

const VermietungBuchungAnfrageForm = ({objectId, isBuchunganfragenPage, adultCount, endDate, gesamtPreis, kidsCount, onAdultCountChange, onEndDateChange, onKidsCountChange, onStartDateChange, startDate}: VermietungBuchungAnfrageFormProps) => {
	const [onClickLocked, setOnClickLocked] = useState(false);

	const isRestrictedBookingRange = (start: Date, end: Date) => {
		return rangeTouchesRestrictedBookingSeason(start, end);
	};

	const isValidRestrictedBookingRange = (start: Date, end: Date) => {
		const nights = getStayInNights(start, end);

		return isSaturday(start) && isSaturday(end) && nights >= 7 && nights % 7 === 0;
	};

	const isDateSelectable = (date: Date) => {
		const currentDate = getStartOfDay(date);
		const dateKey = getDateKey(currentDate);

		if (isDateInIntervals(currentDate, bookedDates)) {
			return false;
		}

		if (startDate && !endDate) {
			const selectedStartDate = getStartOfDay(startDate);

			if (currentDate.getTime() <= selectedStartDate.getTime()) {
				return false;
			}

			if (bookingBoundaryDateKeys.departureDateKeys.has(dateKey)) {
				return false;
			}

			if (!isRestrictedBookingRange(selectedStartDate, currentDate)) {
				return true;
			}

			return isValidRestrictedBookingRange(selectedStartDate, currentDate);
		}

		if (bookingBoundaryDateKeys.arrivalDateKeys.has(dateKey)) {
			return false;
		}

		if (isDateInRestrictedBookingSeason(currentDate)) {
			return isSaturday(currentDate);
		}

		return true;
	};

	const getCalendarDayClassName = (date: Date) => {
		const classNames = [];
		const dateKey = getDateKey(date);
		const isExistingBookingArrival = bookingBoundaryDateKeys.arrivalDateKeys.has(dateKey);
		const isExistingBookingDeparture = bookingBoundaryDateKeys.departureDateKeys.has(dateKey);

		if (isDateInRestrictedBookingSeason(date)) {
			classNames.push("react-datepicker__day--excluded-in-season");
		}

		if (isDateInIntervals(date, bookedDates) || (isExistingBookingArrival && isExistingBookingDeparture)) {
			classNames.push("react-datepicker__day--excluded-unbookable");
		}

		if (isExistingBookingDeparture && !isExistingBookingArrival) {
			classNames.push("booking-calendar__day--bookable-after-checkout");
		}

		if (isSameDay(date, startDate) && isSameDay(date, endDate)) {
			return classNames.join(" ");
		}

		if (isSameDay(date, startDate)) {
			classNames.push("booking-calendar__day--split-start");
		}

		if (isSameDay(date, endDate)) {
			classNames.push("booking-calendar__day--split-end");
		}

		return classNames.join(" ");
	};

	const onDateRangeChange = (dates: [Date | null, Date | null]) => {
		const [start, end] = dates;

		if (start && end && isRestrictedBookingRange(start, end) && !isValidRestrictedBookingRange(start, end)) {
			onStartDateChange(start);
			onEndDateChange(null);
			return;
		}

		if (start !== startDate) {
			onStartDateChange(start);
		}
		if (end !== endDate) {
			onEndDateChange(end);
		}
		if (start && end && start !== end) {
			datePickerFromRef.current?.setState(prev => ({...prev, open: false}))
			datePickerToRef.current?.setState(prev => ({...prev, open: false}))
			setOnClickLocked(true);
			setTimeout(() => {
				setOnClickLocked(false)
			}, 500)
		}
	};

	const {t} = useTranslation();
	const {currentScreenAsNumber} = useMediaQuery();

	const router = useRouter();
	const {data: lastMinutes, isLoading: lastMinutesLoading, refetch} = useQuery(
		["lastMinutes", {[Filter.Rent.queryKey]: 1, locale: router.locale}],
		(): Promise<objectsAttributes[]> => {
			return fetchItem(
				"last-minutes",
				{
					[Filter.Rent.queryKey]: 1
				},
				router.locale
			);
		}
	);

	const ExampleCustomInputFrom = forwardRef(
		({value, onClick, isErrorFrom}: any, ref): any => {
			return (
				<button
					type="button"
					className={`w-full shadow-md flex justify-between items-center p-2 ${
						isErrorFrom ? "border-red-500 border-solid border-2" : "border-none"
					}`}
					onClick={() => {
						if (!onClickLocked) {
							onClick()
						}
					}}
					ref={ref as any}
				>
					{value.toString().split("-")[0]}
					<IconCalender className="w-5"/>
				</button>
			);
		}
	);
	ExampleCustomInputFrom.displayName = "ExampleCustomInput";

	const ExampleCustomInputTo = forwardRef(
		({value, onClick, isErrorTo}: any, ref): any => {

			return (
				<button
					type="button"
					className={`w-full shadow-md flex justify-between items-center p-2 ${
						isErrorTo ? "border-red-500 border-solid border-2" : "border-none"
					}`}
					onClick={() => {
						if (!onClickLocked) {
							onClick()
						}
					}}
					ref={ref as any}
				>
					{value.toString().split("-").slice(-1)[0]}
					<IconCalender className="w-5"/>
				</button>
			);
		}
	);
	ExampleCustomInputTo.displayName = "ExampleCustomInput";

	const [isErrorTo, setIsErrorTo] = useState(false);
	const [isErrorFrom, setIsErrorFrom] = useState(false);

	function onHandleBuchungAnfragen() {
		if (startDate && endDate) {
			router.push({
				pathname: "/vermietung/buchungsanfrage",
				query: {
					startDate: getDateKey(startDate),
					endDate: getDateKey(endDate),
					adultCount: adultCount,
					kidsCount: kidsCount,
					objectId: objectId.toString()
				}
			});
		} else {
			if (!startDate) {
				setIsErrorFrom(true);
			}
			if (!endDate) {
				setIsErrorTo(true);
			}
			window.scrollTo(0, 0);
		}
	}

	const queryClient = useQueryClient();

	const {data: object} = useQuery(
		["object", objectId.toString()],
		() => getObject(objectId.toString()),
		{
			onSuccess: (object: objectsAttributes) => {
				queryClient.setQueryData(["object", objectId.toString()], object);
			}
		}
	);

	const [lastMinutePricePerWeek, setLastMinutePricePerWeek] = useState(0);
	const [normalPricePerWeek, setNormalPricePerWeek] = useState(0);

	const {data: seasonDates, isLoading} = useQuery(
		["seasonDates"],
		getSeasonDates
	);

	const [zuZahlenBeiBuchungPreis, zuZahlenBeiAnkunftPreis] =
		calcZuZahlenBeiBuchungUndAnkunft(gesamtPreis ?? 0);

	const {data: filtedBookingsByObjectId} = useQuery(["bookings", objectId.toString()], () => getBookings(objectId.toString()));

	const bookedDates: { start: Date; end: Date }[] | undefined = getFullyBookedDateIntervals(filtedBookingsByObjectId);
	const bookingBoundaryDateKeys = getBookingBoundaryDateKeys(filtedBookingsByObjectId);

	const [maxDate, setMaxDate] = useState<Date | null>(null);

	useEffect(() => {
		let _startDate = startDate;
		let _endDate = endDate;

		if (object && seasonDates) {
			const [_normalPrice, _specialPrice] = calcNormalRentPrice(
				object,
				seasonDates,
				_startDate,
				_endDate
			);

			if (!_startDate || !_endDate) {
				setNormalPricePerWeek(_normalPrice);
				setLastMinutePricePerWeek(_specialPrice);
			} else {
				setNormalPricePerWeek(_normalPrice + parseInt(object.end_cleaning));
				setLastMinutePricePerWeek(
					_specialPrice > 0
						? _specialPrice + parseInt(object.end_cleaning)
						: _specialPrice
				);
			}
		}
	}, [
		object,
		seasonDates,
		startDate,
		endDate,
		bookedDates,
		isBuchunganfragenPage
	]);

	const datePickerFromRef = createRef<DatePicker<"", true>>()
	const MemotizedDatePickerFrom = <DatePicker
		ref={datePickerFromRef}
		dateFormat="dd.MM.yyyy"
		selected={startDate}
		filterDate={isDateSelectable}
		onChange={(dates) => {
			onDateRangeChange(dates);
			const startDateInside = dates?.[0];
			if (startDateInside) {
				setMaxDate(getNextBookedArrivalDate(filtedBookingsByObjectId, startDateInside));
			}
			if (dates[1]) setMaxDate(null);
		}}
		calendarClassName="!border-none p-7 md:p-10 !flex shadow-md md:!text-lg !font-sans md:!text-[18px] !tracking-[2.7px]"
		renderCustomHeader={(props) => (
			<>
				<CustomHeader
					twoMonth={currentScreenAsNumber > ScreenNumber.lg}
					{...props}
				/>
			</>
		)}
		startDate={startDate}
		endDate={endDate}
		renderDayContents={(day, date) => (
			<div className="flex-1 ">
				<p className="font-normal !text-[18px]">{day}</p>
			</div>
		)}
		dayClassName={getCalendarDayClassName}
		selectsRange
		calendarStartDay={1}
		excludeDateIntervals={bookedDates}
		customInput={<ExampleCustomInputFrom isErrorFrom={isErrorFrom}/>}
		locale={router.locale}
		showPopperArrow={false}
		monthsShown={currentScreenAsNumber > ScreenNumber.lg ? 2 : 1}
		maxDate={maxDate}
		disabledKeyboardNavigation
		minDate={new Date()}
	/>

	const datePickerToRef = createRef<DatePicker<"", true>>()
	const MemotizedDatePickerTo = <DatePicker
		ref={datePickerToRef}
		dateFormat="dd.MM.yyyy"
		selected={endDate}
		filterDate={isDateSelectable}

		onChange={(dates) => {
			onDateRangeChange(dates);
			const startDateInside = dates?.[0];
			if (startDateInside) {
				setMaxDate(getNextBookedArrivalDate(filtedBookingsByObjectId, startDateInside));
			}
			if (dates[1]) {
				setMaxDate(null);
			}
		}}
		calendarClassName="!border-none p-7 md:p-10 !flex shadow-md md:!text-lg !font-sans md:!text-[18px] !tracking-[2.7px]"
		renderCustomHeader={(props) => (
			<>
				<CustomHeader
					twoMonth={currentScreenAsNumber > ScreenNumber.lg}
					{...props}
				/>
			</>
		)}
		startDate={startDate}
		endDate={endDate}
		renderDayContents={(day, date) => (
			<div className="flex-1 ">
				<p className="font-normal !text-[18px]">{day}</p>
			</div>
		)}
		dayClassName={getCalendarDayClassName}
		selectsRange
		excludeDateIntervals={bookedDates}
		calendarStartDay={1}
		customInput={<ExampleCustomInputTo isErrorTo={isErrorTo}/>}
		minDate={new Date(Date.now())}
		locale={router.locale}
		showPopperArrow={false}
		monthsShown={currentScreenAsNumber > ScreenNumber.lg ? 2 : 1}
		disabledKeyboardNavigation
		maxDate={maxDate}
	/>

	const datePickerRef = createRef<DatePicker<"", true>>()
	const MemotizedDatePicker = <DatePicker
		ref={datePickerRef}
		dateFormat="dd.MM.yyyy"
		selected={endDate}
		filterDate={isDateSelectable}

		onChange={(dates) => {
			onDateRangeChange(dates);
			const startDateInside = dates?.[0];
			if (startDateInside) {
				setMaxDate(getNextBookedArrivalDate(filtedBookingsByObjectId, startDateInside));
			}
			if (dates[1]) {
				setMaxDate(null);
			}
		}}
		calendarClassName="!border-none p-7 md:p-10 md:pb-0 !flex md:!text-lg md:!text-[18px] justify-between !rounded-[20px] gap-5"
		renderCustomHeader={(props) => (
			<>
				<CustomHeaderThreeMonths
					twoMonth={currentScreenAsNumber > ScreenNumber.lg}
					{...props}
				/>
			</>
		)}
		startDate={startDate}
		endDate={endDate}
		renderDayContents={(day, date) => (
			<div className="flex-1 ">
				<p className="!text-[18px]">{day}</p>
			</div>
		)}
		dayClassName={getCalendarDayClassName}
		selectsRange
		excludeDateIntervals={bookedDates}
		calendarStartDay={1}
		// customInput={<ExampleCustomInputTo isErrorTo={isErrorTo}/>}
		minDate={new Date(Date.now())}
		locale={router.locale}
		showPopperArrow={false}
		monthsShown={currentScreenAsNumber > ScreenNumber.lg ? 3 : 1}
		disabledKeyboardNavigation
		inline
	/>

	const noDateSelected = !startDate && !endDate;
	const showLastMinutePrice = lastMinutePricePerWeek > 0 && lastMinutePricePerWeek <= normalPricePerWeek;
	const priceTagWhenNoDateRangeIsSelected = t("common.priceFrom", {
		price: showLastMinutePrice ? lastMinutePricePerWeek.toLocaleString(router.locale) : normalPricePerWeek.toLocaleString(router.locale)
	});

	return (
		<div className={`${!isBuchunganfragenPage ? "flex" : "flex"} xl:flex xl:flex-col`}>
			<div className="relative">
				<div className={`${!isBuchunganfragenPage ? 
					"static top-0 left-0 right-0 justify-self-center flex flex-col xl:-mt-[30px] w-full bg-white xl:max-w-[90%] mx-auto rounded-[20px] shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)]"
					:
					"flex flex-col p-4 xl:py-[49px] xl:px-[76px] space-y-10 xl:flex xl:flex-col shadow-md h-min xl:max-w-[425px]"
				}`}>
					{isBuchunganfragenPage && (
						<>
							<label className="space-y-2">
								<span className="">{t("components.vermietungBuchungAnfrageForm.arrival")}</span>{MemotizedDatePickerFrom}
							</label>
							<label className="space-y-2">
								<span className="">{t("components.vermietungBuchungAnfrageForm.departure")}</span>{MemotizedDatePickerTo}
							</label>
						</>
					)}
					{!isBuchunganfragenPage && (
						<>
							{MemotizedDatePicker}
						</>
					)}
					<div className={`${!isBuchunganfragenPage ? 
						"flex justify-between items-end px-10 pt-4 pb-10 flex-wrap" 
						: 
						"flex flex-col justify-center"
					}`}>
						<div className="flex flex-row items-center gap-4">
							<label className="space-y-2 w-auto max-w-[150px]"><span className=" whitespace-nowrap font-light text-[20px]">{t("components.vermietungBuchungAnfrageForm.adults")}</span>
								<input
									type="number"
									min={1}
									value={adultCount}
									onChange={(e) => onAdultCountChange(parseInt(e.target.value))}
									className=" w-full border-none shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)] input-show-arrows"
								/>
							</label>
							<label className="space-y-2 w-auto max-w-[150px]"><span className=" whitespace-nowrap font-light text-[20px]">{t("components.vermietungBuchungAnfrageForm.children")}</span>
								<input
									type="number"
									min={0}
									value={kidsCount}
									onChange={(e) => onKidsCountChange(parseInt(e.target.value))}
									className=" w-full border-none shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)] input-show-arrows"
								/>
							</label>
						</div>
						{isBuchunganfragenPage && (
							<div className="flex flex-col divide-y-2 items-center justify-between w-full">
								<div className="w-full py-5 flex flex-row justify-between items-center [&>*]:text-lg [&>*]:text-leo-divider-gray [&>*]:font-bold gap-x-2">
									<p>{t("components.vermietungBuchungAnfrageForm.totalPrice")}</p>
									<p className="whitespace-nowrap">
										{(gesamtPreis?.toLocaleString(router.locale) ?? 0) + " €"}
									</p>
								</div>
								<div className="w-full py-5 flex flex-row justify-between items-center [&>*]:text-lg [&>*]:text-leo-divider-gray [&>*]:font-bold gap-x-2">
									<p>{t("components.vermietungBuchungAnfrageForm.toPayOnArrival")}</p>
									<p className="whitespace-nowrap">
										{zuZahlenBeiAnkunftPreis.toLocaleString(router.locale) + " €"}
									</p>
								</div>
								<div className="w-full py-5 flex flex-row justify-between items-center  [&>*]:font-bold [&>*]:text-leo-orange gap-x-2">
									<p className="uppercase text-xl">
										{t("components.vermietungBuchungAnfrageForm.toPayOnBooking")}
									</p>
									<p className="whitespace-nowrap text-3xl">
										{zuZahlenBeiBuchungPreis.toLocaleString(router.locale) + " €"}
									</p>
								</div>
							</div>
						)}

						{!isBuchunganfragenPage && (
							<div className="flex flex-col space-y-10">
								<div className="flex gap-6 whitespace-nowrap items-center flex-wrap">
									{showLastMinutePrice && normalPricePerWeek > 0 && (
										<h3
											className={`line-through text-leo-text-gray font-light text-[40px] decoration-1`}
										>
											{t("common.price", {
												price: normalPricePerWeek.toLocaleString(router.locale)
											})}
										</h3>
									)}
									{(lastMinutePricePerWeek > 0 || normalPricePerWeek > 0) && (
										<div className="flex flex-row items-center space-x-3 ">
											<h3 className="text-leo-blue font-bold text-[40px]">
												{t(noDateSelected ? "common.priceFrom" : "common.price", {
													price: showLastMinutePrice
														? lastMinutePricePerWeek.toLocaleString(router.locale)
														: normalPricePerWeek.toLocaleString(router.locale)
												})}
											</h3>
											{showLastMinutePrice && (
												<p className="font-bold text-leo-orange">
													{t(
														"components.vermietungBuchungAnfrageForm.lastMinutePrice"
													)}
												</p>
											)}
										</div>
									)}
									{noDateSelected && (
										<p className=" text-leo-text-gray">
											{t("components.vermietungBuchungAnfrageForm.perWeek")}
										</p>
									)}
								</div>
							</div>
						)}
					</div>

					{!isBuchunganfragenPage && (
						<div className="bg-[#e0f5fc] flex justify-between items-center px-12 py-8 rounded-br-[20px] rounded-bl-[20px] flex-wrap gap-y-3">
							<SocialMediaButtonGroup source="contact_rent"/>
							<Button
								text={t(
									"components.vermietungBuchungAnfrageForm.sendBookingRequest"
								)}
								transparent={false}
								border={false}
								height={"h-[43px]"}
								padding={"px-4"}
								className="rounded-[10px] text-[12px] font-bold leading-normal hover:bg-leo-dark-brown hover:from-transparent hover:to-transparent"
								onClick={onHandleBuchungAnfragen}
								textTransform={"uppercase"}
							/>
						</div>
					)}
					
				</div>
			</div>
			
		</div>
	);
}

export default memo(VermietungBuchungAnfrageForm);
